<script setup lang="ts">
import { followUser, getUserInformation, unfollowUser } from '~~/src/api/users'
import type { UserProfile } from '~~/src/types/user'

interface IProps {
  user: UserProfile
  variant?: 'default' | 'small'
}

const props = withDefaults(defineProps<IProps>(), { variant: 'default' })
const { user, variant } = toRefs(props)
const buttonRef = ref<HTMLDivElement>()

const { colors } = useTheme()
const router = useRouter()
const isHovering = process.client ? useElementHover(buttonRef) : false

const { firebaseUser } = useFirebaseAuth()

const isFollowing = ref<boolean | null>(user.value.isFollowed ?? null)
const isLoading = ref(false)

watch(user, () => {
  isFollowing.value = user.value.isFollowed || null
})

watch(firebaseUser, async () => {
  await until(user).toMatch(v => !!v, {
    timeout: 3000,
  })

  if (!user.value)
    return

  if (!firebaseUser) {
    isFollowing.value = false
    return
  }

  const userInfo = await getUserInformation([user.value.id])
  if (userInfo?.length)
    isFollowing.value = userInfo[0].isFollowed
})

async function handleFollow() {
  if (!user.value || isLoading.value)
    return

  if (!firebaseUser.value)
    return router.push(useGetLoginURL())

  isLoading.value = true

  if (isFollowing.value) {
    const success = await unfollowUser(user.value.id)

    success
      ? isFollowing.value = false
      : isFollowing.value = true
  }

  else {
    const success = await followUser(user.value.id)

    success
      ? isFollowing.value = true
      : isFollowing.value = false
  }

  isLoading.value = false
}
</script>

<template>
  <button
    ref="buttonRef"
    flex center flex-shrink-0 py="5px" min-w="16" :h="variant === 'small' ? 6 : 10"
    border border-soft-gray rounded-full bg-white
    cursor-pointer duration-125 transition-colors text-bluish-darker
    class="follow-button"
    :class="{ 'is-active': isFollowing }"
    @click="handleFollow"
  >
    <div v-if="isLoading" flex center w-8>
      <div animate-spin :style="{ animationDuration: '2s' }">
        <IconSpinner :size="10" :color="colors.darkerGray" />
      </div>
    </div>
    <template v-else>
      <div
        :pr="variant === 'small' ? 1 : 2"
        :pl="variant === 'small' ? 3 : 4"
        flex items-center gap-1
      >
        <Text v-if="variant === 'small'" variant="caption">
          <TransitionFade>
            {{ !isFollowing ? 'Follow' : (isHovering ? 'Unfollow' : 'Following') }}
          </TransitionFade>
        </Text>
        <Text v-else variant="normal">
          <TransitionFade>
            {{ !isFollowing ? 'Follow' : (isHovering ? 'Unfollow' : 'Following') }}
          </TransitionFade>
        </Text>

        <div
          flex center rounded-full bg-gray-lighter text-bluish-softer transition-colors
          class="follow-button-icon"
          :text="variant === 'small' ? '10px' : '14px'"
          :w="variant === 'small' ? 4 : 6" :h="variant === 'small' ? 4 : 6"
        >
          <div v-if="isHovering && isFollowing" i-mdi:close />
          <div v-else-if="isFollowing" i-mdi:check />
          <div v-else i-mdi:arrow-right />
        </div>
      </div>
    </template>
  </button>
</template>

<style lang="scss">
.follow-button:hover {
  @apply bg-gray-lighter border-gray-lighter text-black-softer;

  .follow-button-icon {
    @apply bg-black-softer text-white;
  }
}

.follow-button:not(.is-active):active {
  background: linear-gradient(45deg, #A993FF 0%, rgba(253, 147, 231, 0.75) 48.96%, #FF88EC 100%);
  @apply border-primary text-white;

  .follow-button-icon {
    @apply bg-white text-primary;
  }
}

.follow-button.is-active:active {
  @apply bg-black-softer border-black-softer text-white;

  .follow-button-icon {
    @apply bg-white text-black-softer;
  }
}
</style>
